import React, { useEffect, useMemo, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'react-hot-toast';
import ToastContainer, { toast } from './components/Toast.jsx';
import { ToastProvider } from './components/ToastNotification.jsx';
import ProfileSidebar from './components/ProfileSidebar.jsx';
import TrustForm from './components/TrustForm.jsx';
import BottomSheet from './components/BottomSheet.jsx';
import TrustModal from './components/TrustModal.jsx';
import PhantomInAppWarning from './components/PhantomInAppWarning.jsx';
import MaintenanceScreen from './components/MaintenanceScreen.jsx';
import {
  FiLogOut,
  FiMenu,
  FiX,
  FiShoppingCart,
  FiBookOpen,
  FiSettings,
  FiBell,
  FiPackage,
  FiHome,
} from 'react-icons/fi';
import NotificationBell from './components/NotificationBell.jsx';
// eslint-disable-next-line no-unused-vars
import LoadingSpinner from './components/LoadingSpinner.jsx';
// API URL設定 (直接埋め込み)
const getApiUrl = () => {
  const LOCALHOST_API = 'http://localhost:3000';
  const WSL_TO_WINDOWS_API = 'http://172.31.224.1:3000';
  // 本番/ステージング Vercel デプロイは別オリジンの Railway バックエンドを叩く必要があるため、
  // 空文字を返すと /api/health が自オリジン(SPA)の index.html を返してしまい
  // メンテナンス画面に固定される。VITE_API_URL を必ず参照する。
  const RAILWAY_API = import.meta.env.VITE_API_URL || 'https://registration-app-production.up.railway.app';

  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  const isWSL =
    window.location.hostname.startsWith('172.') ||
    window.location.hostname.startsWith('192.168.');

  if (isWSL) return WSL_TO_WINDOWS_API;
  if (isLocalhost) return LOCALHOST_API;
  return RAILWAY_API;
};
const API_URL = getApiUrl();
// APIインターセプター設定（統合実装）
const setupApiInterceptor = () => {
  // マイニングエフェクトをトリガー（カスタムイベント経由）
  const triggerMiningEffect = (tokenSymbol, amount, source) => {
    // カスタムイベントをディスパッチしてGlobalMiningEffectに通知
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new window.CustomEvent('tokenMined', {
        detail: {
          tokenSymbol,
          amount,
          source,
          isConsumption: false,
        }
      }));
    }
  };
  
  const triggerTokenConsumption = (amount, purpose) => {
    // カスタムイベントをディスパッチ
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new window.CustomEvent('tokenMined', {
        detail: {
          tokenSymbol: 'SHINKETSU',
          amount,
          source: purpose,
          isConsumption: true,
        }
      }));
    }
  };

  // Axiosインターセプターを設定
  if (typeof window !== 'undefined') {
    axios.interceptors.response.use(
      (response) => {
        // マイニング情報の処理
        if (response.data && response.data.mining) {
          const miningData = response.data.mining;
          if (miningData.success && miningData.amount > 0) {
            triggerMiningEffect(
              'SHINKETSU',
              miningData.amount,
              miningData.action_type || 'mining'
            );
          }
        }

        // トークン消費情報の処理
        if (response.data && response.data.token_consumption) {
          const consumptionData = response.data.token_consumption;
          if (consumptionData.amount > 0) {
            triggerTokenConsumption(
              consumptionData.amount,
              consumptionData.purpose || 'purchase'
            );
            window.dispatchEvent(new window.Event('walletBalanceUpdate'));
          }
        }

        return response;
      },
      (error) => {
        // 401エラーの自動処理
        if (error.response && error.response.status === 401) {
          console.log('🔄 401エラー検出 - 無効なトークンをクリアします');
          
          // ストアとして操作中に取っておいた値も消す（BOARD A-33）
          clearStoreActing();
          localStorage.removeItem('token');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('user');
          
          if (error.response.data && error.response.data.shouldClearStorage) {
            console.log('🗑️ サーバー指示により全認証データをクリア');
            localStorage.removeItem('walletAddress');
            sessionStorage.clear();
          }
          
          if (!window.location.pathname.includes('/login') && 
              !window.location.pathname.includes('/introduction') &&
              !window.location.pathname.includes('/kyc')) {
            console.log('🔄 イントロダクションページにリダイレクト');
            window.location.href = '/introduction';
            return;
          }
        }
        
        return Promise.reject(error);
      }
    );
  }
};
import ScrollToTop from './components/ScrollToTop.jsx';
import CartIcon from './components/CartIcon.jsx';
import MobileFooter from './components/MobileFooter.jsx';
import { useMobileFooter } from './hooks/useMobileFooter';
import TokenDisplay from './components/TokenDisplay.jsx';
import ActorAvatar from './components/ActorAvatar.jsx';
import { useTokenAnimation } from './components/TokenAnimationEffect.jsx';
import GlobalMiningEffect from './components/GlobalMiningEffect.jsx';
import { useMiningNotifications } from './hooks/useMiningNotifications.js';
import {
  NavigationProvider,
  useNavigationHistory,
} from './contexts/NavigationContext.jsx';
import NavigationTracker from './components/NavigationTracker.jsx';
import { ForestProvider } from './contexts/ForestContext.jsx';
import { ProfileSidebarProvider, useProfileSidebar } from './contexts/ProfileSidebarContext.jsx';
import { BottomSheetProvider, useBottomSheet } from './contexts/BottomSheetContext.jsx';
import VerifyMagicLink from './pages/VerifyMagicLink.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import ActingAsStoreBanner from './components/ActingAsStoreBanner.jsx';
import { clearStoreActing } from './utils/storeActing.js';
import QuestionForest from './pages/QuestionForest.jsx';
import KindleBonus from './pages/KindleBonus.jsx';
import KYCVerification from './pages/admin/KYCVerification.jsx';
import DaoApplicationReview from './pages/admin/DaoApplicationReview.jsx';
// DashboardResponsive archived - no longer used

// Basic wallet functionality with error handling
const WalletModalProvider = ({ children }) => children;

// 認証状態に基づくリダイレクトコンポーネント
const AuthRedirect = () => {
  const token = localStorage.getItem('token');
  const user = localStorage.getItem('user');

  if (token && user) {
    // 認証済みユーザーはダッシュボードへ
    return <Navigate to="/dashboard" replace />;
  } else {
    // 未認証ユーザーはイントロダクションへ
    return <Navigate to="/introduction" replace />;
  }
};

// JWT安全デコード関数
// eslint-disable-next-line no-unused-vars
const safeJWTDecode = (token) => {
  try {
    if (!token || typeof token !== 'string') {
      throw new Error('Invalid token format');
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT structure');
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4;
    const paddedBase64 = padding ? base64 + '='.repeat(4 - padding) : base64;

    const jsonPayload = decodeURIComponent(
      window
        .atob(paddedBase64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('❌ JWT decode error:', error);
    return null;
  }
};


// Enhanced error handling for Phantom wallet extension
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (
      event.filename &&
      event.filename.includes('solanaActionsContentScript')
    ) {
      // Handled Phantom wallet extension error
      event.preventDefault();
      return false;
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason?.toString().toLowerCase() || '';
    
    if (
      reason.includes('solana') ||
      reason.includes('serviceworker') ||
      reason.includes('service worker')
    ) {
      // Handled Solana-related and Service Worker promise rejections
      event.preventDefault();
    }
  });
}

// Pages imports
const Register = React.lazy(() => import('./pages/Register.jsx'));
const ProjectDetail = React.lazy(() => import('./pages/ProjectDetail.jsx'));
const TaskRecruitmentForm = React.lazy(() => import('./pages/TaskRecruitmentForm.jsx'));
const TaskManagement = React.lazy(() => import('./pages/TaskManagement.jsx'));
const IssueManagement = React.lazy(() => import('./pages/IssueManagement.jsx'));
const RiskManagement = React.lazy(() => import('./pages/RiskManagement.jsx'));
const IssueDetail = React.lazy(() => import('./pages/IssueDetail.jsx'));
const RiskDetail = React.lazy(() => import('./pages/RiskDetail.jsx'));
const ProjectManagement = React.lazy(() => import('./pages/ProjectManagement.jsx'));
// Login functionality is now handled through wallet connection in Introduction page

const Introduction = React.lazy(() => import('./pages/Introduction.jsx'));
const SignIn = React.lazy(() => import('./pages/SignIn.jsx'));
const AdminLogin = React.lazy(() => import('./pages/AdminLogin.jsx'));
const AdminMiningDebug = React.lazy(() => import('./pages/AdminMiningDebug.jsx'));
// VerifyMagicLinkは上部で直接インポート済み
const WalletRecovery = React.lazy(() => import('./pages/WalletRecovery.jsx'));
const Kyc = React.lazy(() => import('./pages/Kyc.jsx'));
const ActorIntegratedDashboard = React.lazy(
  () => import('./pages/ActorIntegratedDashboard.jsx')
);
const DashboardContainer = React.lazy(
  () => import('./pages/dashboard/DashboardContainer.jsx')
);
const ForestCategoryPage = React.lazy(
  () => import('./pages/dashboard/categories/ForestCategoryPage.jsx')
);
const ProjectsCategoryPage = React.lazy(
  () => import('./pages/dashboard/categories/ProjectsCategoryPage.jsx')
);
const StoreCategoryPage = React.lazy(
  () => import('./pages/dashboard/categories/StoreCategoryPage.jsx')
);
const SocialCategoryPage = React.lazy(
  () => import('./pages/dashboard/categories/SocialCategoryPage.jsx')
);
const ApplicationsCategoryPage = React.lazy(
  () => import('./pages/dashboard/categories/ApplicationsCategoryPage.jsx')
);
const AdminCategoryPage = React.lazy(
  () => import('./pages/dashboard/categories/AdminCategoryPage.jsx')
);
const Profile = React.lazy(() => import('./pages/Profile.jsx'));
const TrustNetwork = React.lazy(() => import('./pages/TrustNetwork.jsx'));
const Timeline = React.lazy(() => import('./pages/Timeline.jsx'));
const TokenSpendingTimeline = React.lazy(() => import('./pages/TokenSpendingTimeline.jsx'));
const TokenSpendingTrending = React.lazy(() => import('./pages/TokenSpendingTrending.jsx'));
const TokenSpendingMyFeed = React.lazy(() => import('./pages/TokenSpendingMyFeed.jsx'));
const FeedThreadDetail = React.lazy(() => import('./pages/FeedThreadDetail.jsx'));
const AllDiscussions = React.lazy(() => import('./pages/AllDiscussions.jsx'));
const IntegratedDiscussion = React.lazy(
  () => import('./pages/IntegratedDiscussion')
);
const Wallet = React.lazy(() => import('./pages/Wallet.jsx'));
const StoreListView = React.lazy(() => import('./pages/StoresView.jsx'));
const StoreDetail = React.lazy(() => import('./pages/StoreDetailView.jsx'));
const ActorNetworkView = React.lazy(() => import('./pages/ActorNetworkView.jsx'));
const StoreRecruitmentSettings = React.lazy(
  () => import('./pages/StoreRecruitmentSettings')
);
const ProductSearch = React.lazy(() => import('./pages/ProductSearch.jsx'));
const Cart = React.lazy(() => import('./pages/Cart.jsx'));
const DesirePool = React.lazy(() => import('./pages/DesirePool.jsx'));
const MyStoreView = React.lazy(() => import('./pages/MyStoreView.jsx'));
const StoreEdit = React.lazy(() => import('./pages/StoreEdit.jsx'));
const MyStoreItemManagement = React.lazy(() => import('./pages/MyStoreItemManagement.jsx'));
const MyStoreItemsManagement = React.lazy(() => import('./pages/MyStoreItemsManagement.jsx'));
const StoreOrderManagement = React.lazy(() => import('./pages/StoreOrderManagement.jsx'));
const DaoListView = () => 
  <div className="p-8">
    <h1>DAO一覧</h1>
    <p>実装予定です</p>
  </div>
;
const DaoDetail = React.lazy(() => import('./pages/DaoDetail.jsx'));
const DaoMembers = () => 
  <div className="p-8">
    <h1>DAOメンバー</h1>
    <p>実装予定です</p>
  </div>
;
const DaoRecruitmentSettings = React.lazy(
  () => import('./pages/DaoRecruitmentSettings')
);
const DaoInvitations = React.lazy(() => import('./pages/DaoInvitations.jsx'));
const ApplicationsList = React.lazy(() => import('./pages/ApplicationsList.jsx'));
const DaoGovernance = React.lazy(() => import('./pages/DaoGovernance.jsx'));
const DaoApplication = React.lazy(() => import('./pages/DaoApplication.jsx'));
const DerivedDaoCreateView = React.lazy(
  () => import('./pages/DerivedDaoCreateView')
);
const DerivedDaoListView = React.lazy(
  () => import('./pages/DerivedDaoListView')
);
const ChatMenu = React.lazy(() => import('./pages/ChatMenu.jsx'));
const GroupChatRoom = React.lazy(() => import('./pages/GroupChatRoom.jsx'));
const StoreChat = React.lazy(() => import('./pages/StoreChat.jsx'));
const Notifications = React.lazy(() => import('./pages/Notifications.jsx'));
const GroupChatList = () => 
  <div className="p-8">
    <h1>グループチャット一覧</h1>
    <p>実装予定です</p>
  </div>
;
const SpecialRewards = React.lazy(() => import('./pages/SpecialRewards.jsx'));
const SpecialRewardDetail = () => 
  <div className="p-8">
    <h1>特別報酬詳細</h1>
    <p>実装予定です</p>
  </div>
;
const PracticeGuide = React.lazy(() => import('./pages/PracticeGuide.jsx'));
const StoreApplicationView = React.lazy(
  () => import('./pages/StoreApplicationView')
);
const StoreApplicationStatusView = React.lazy(
  () => import('./pages/StoreApplicationStatusView')
);
const StoreApplicationDetailView = React.lazy(
  () => import('./pages/StoreApplicationDetailView')
);
const StoreAppealView = React.lazy(() => import('./pages/StoreAppealView.jsx'));
const ProjectList = React.lazy(() => import('./pages/ProjectList.jsx'));
const RealizationJudge = React.lazy(() => import('./pages/RealizationJudge.jsx'));
const BadgeHub = React.lazy(() => import('./pages/BadgeHub.jsx'));
const BadgeDetail = React.lazy(() => import('./pages/BadgeDetail.jsx'));
const BadgeHolders = React.lazy(() => import('./pages/BadgeHolders.jsx'));
const TitleHub = React.lazy(() => import('./pages/TitleHub.jsx'));
const TitleDetail = React.lazy(() => import('./pages/TitleDetail.jsx'));
const Subscriptions = React.lazy(() => import('./pages/Subscriptions.jsx'));
const QuestionDetail = React.lazy(() => import('./pages/QuestionDetail.jsx'));
const ForestEcosystemVisualization = React.lazy(
  () => import('./pages/ForestEcosystemVisualization')
);
const PurchaseHistory = React.lazy(() => import('./pages/PurchaseHistory.jsx'));
const BookReadingHub = React.lazy(() => import('./pages/BookReadingHub.jsx'));
const BookReadingProgress = React.lazy(
  () => import('./pages/BookReadingProgress')
);
const ChapterQuiz = React.lazy(() => import('./pages/ChapterQuiz.jsx'));
const BookCertificate = React.lazy(() => import('./pages/BookCertificate.jsx'));
// 他の不足コンポーネント
const RecruitmentListView = React.lazy(
  () => import('./pages/RecruitmentListView')
);
const RecruitmentDetail = React.lazy(() => import('./pages/RecruitmentDetail.jsx'));
const FriendsList = React.lazy(() => import('./pages/FriendsList.jsx'));
const MyStakes = React.lazy(() => import('./pages/MyStakes.jsx'));
const Activity = React.lazy(() => import('./pages/Activity.jsx'));
const SystemParameterSettings = React.lazy(
  () => import('./pages/SystemParameterSettings')
);
const UserDetail = () => 
  <div className="p-8">
    <h1>ユーザー詳細</h1>
    <p>実装予定です</p>
  </div>
;
const PostDetail = () => 
  <div className="p-8">
    <h1>投稿詳細</h1>
    <p>実装予定です</p>
  </div>
;
const StoreSearch = () => 
  <div className="p-8">
    <h1>ストア検索</h1>
    <p>実装予定です</p>
  </div>
;
const StoreItemDetailView = React.lazy(
  () => import('./pages/StoreItemDetailView')
);
const StoreItemEditView = React.lazy(() => import('./pages/StoreItemEditView.jsx'));
const TransferEntryForm = () => 
  <div className="p-8">
    <h1>送金フォーム</h1>
    <p>実装予定です</p>
  </div>
;
const BadgeRequest = () => 
  <div className="p-8">
    <h1>バッジリクエスト</h1>
    <p>実装予定です</p>
  </div>
;
const SearchIntegration = () => 
  <div className="p-8">
    <h1>統合検索</h1>
    <p>実装予定です</p>
  </div>
;
const NotFound = React.lazy(() => import('./pages/NotFound.jsx'));

import './App.css';
import 'react-image-crop/dist/ReactCrop.css';

// APIインターセプターを設定（401エラー時の自動トークンクリア）
setupApiInterceptor();

const App = () => {
  return (
    <ToastProvider>
      <Toaster position="top-center" />
      <PhantomInAppWarning />
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ProfileSidebarProvider>
          <BottomSheetProvider>
            <NavigationProvider>
              <ForestProvider>
                <ScrollToTop />
                <NavigationTracker />
                <AppContent />
              </ForestProvider>
            </NavigationProvider>
          </BottomSheetProvider>
        </ProfileSidebarProvider>
      </Router>
    </ToastProvider>
  );
};


const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { navigationHistory, getBreadcrumbs } = useNavigationHistory();
  const { AnimationRenderer } = useTokenAnimation();
  const { openBottomSheet } = useBottomSheet();
  const { openProfile } = useProfileSidebar();
  const [user, setUser] = React.useState(null);
  
  useMiningNotifications();
  const [loading, setLoading] = React.useState(true);
  const [cartItemCount, setCartItemCount] = React.useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false); // モバイルメニューの開閉状態
  const [isMobile, setIsMobile] = React.useState(false); // モバイル判定
  const [isMounted, setIsMounted] = React.useState(false); // マウント状態
  const { showFooter } = useMobileFooter();
  // 初期描画のちらつき防止用の状態
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);
  // 地球儀アイコンの回転は不要のため削除
  // メンテナンスモード状態
  const [isMaintenanceMode, setIsMaintenanceMode] = React.useState(false);

  // 現在のパスから画面名と副題、パンくず情報を取得する関数
  const getPageInfo = (pathname) => {
    const pageMap = {
      '/dashboard': { title: 'ダッシュボード', subtitle: 'あなたの森の現在地' },
      '/dashboard/forest': { title: '🌲 森の箱庭', subtitle: '問いの森・需要の発酵池' },
      '/dashboard/projects': { title: '📂 案件', subtitle: 'DAO・プロジェクト・DAO招待' },
      '/dashboard/store': { title: '🏪 ストア/報酬', subtitle: 'ストア一覧・マイストア・報酬履歴・支出フィード' },
      '/dashboard/social': { title: '🌐 ソーシャル', subtitle: '友達・バッジ・肩書き・ディスカッション・チャット' },
      '/dashboard/applications': { title: '📂 案件・申請・募集', subtitle: 'プロジェクト・応募一覧・募集一覧' },
      '/dashboard/admin': { title: '🔧 システム管理', subtitle: 'パラメーター設定・ユーザー管理・マイニングデバッグ' },
      '/signin': { title: '🌱 信頼の種を植える儀式', subtitle: 'あなたを準会員として受け入れる儀式' },
      '/kyc': { title: '本登録（KYC）', subtitle: '本会員への昇格手続き' },
      '/applications': {
        title: '応募一覧',
        subtitle: 'あなたが応募した募集の一覧と状況を確認できます',
      },
      '/store-application-status': {
        title: '申請状況照会',
        subtitle: '発酵に向けた審査ログ',
      },
      '/store-application': {
        title: 'ストア申請',
        subtitle: '意志ある商店の芽生え',
      },
      '/discussions': {
        title: 'ディスカッション',
        subtitle: '問いを育てる広場',
      },
      '/projects': {
        title: 'プロジェクト一覧',
        subtitle: 'DAOに実る問いの果実',
      },
      '/projects/create': {
        title: 'プロジェクト登録',
        subtitle: '新たな問いから行動への芽生え',
      },
      '/question-forest': {
        title: '問いの森',
        subtitle: 'まだ名前のない気づきが芽吹く場所',
      },
      '/questions/create': {
        title: '問いを植える',
        subtitle: 'まだ名前のない気づきが芽吹く場所',
      },
      '/wallet': { title: 'ウォレット', subtitle: '共鳴と振る舞いの記憶庫' },
      '/forest-ecosystem': {
        title: 'エコサイクル・マイニングとは',
        subtitle: 'エコサイクル・マイニングの循環構造',
      },
      '/mystakes': { title: '信頼の使途', subtitle: '貢献が信頼に変わる記録' },
      '/mystore': {
        title: 'マイストア',
        subtitle: '運営と発酵のダッシュボード',
      },
      '/my-stores': {
        title: 'マイストア',
        subtitle: 'あなたのストアを管理する',
      },
      '/store-recruitment-settings': {
        title: '募集設定',
        subtitle: '意味のある仲間を求めて',
      },
      '/store-search': {
        title: 'ストア検索',
        subtitle: '共感と信頼のある商店を見つける',
        breadcrumb: {
          parent: '/store',
          parentTitle: 'ストア一覧',
        },
      },
      '/stores': { title: 'ストア一覧', subtitle: '意味ある商店が集う場所' },
      '/product-search': {
        title: '商品検索',
        subtitle: '意味で選ぶ買い物',
      },
      '/cart': {
        title: 'カート',
        subtitle: '選んだ商品を確認',
      },
      '/purchase-history': {
        title: '購入履歴',
        subtitle: '確定した注文の一覧',
      },
      '/store-orders': {
        title: '注文管理',
        subtitle: 'あなたのストアに届いた注文を管理します',
      },
      '/store/items/new': {
        title: '新規商品追加',
        subtitle: '新しい商品をストアに追加する',
        breadcrumb: {
          parent: '/mystore',
          parentTitle: 'マイストア',
        },
      },
      '/store/edit/:storeId': {
        title: 'ストア編集',
        subtitle: 'ストア情報を編集する',
        breadcrumb: {
          parent: '/mystore',
          parentTitle: 'マイストア',
        },
      },
      '/token-spending': {
        title: '支出フィード',
        subtitle: 'コミュニティのトークン使い道を共有し、透明性と信頼を育てる場所',
        subNavigationTabs: [
          {
            label: 'マイフィード',
            icon: '👤',
            path: '/token-spending/my-feed',
          },
          {
            label: 'タイムライン',
            icon: '🕐',
            path: '/token-spending/timeline',
          },
          {
            label: 'トレンド',
            icon: '🔥',
            path: '/token-spending/trending',
          },
        ],
      },
      '/token-spending/my-feed': {
        title: '支出フィード',
        subtitle: 'コミュニティのトークン使い道を共有し、透明性と信頼を育てる場所',
        subNavigationTabs: [
          {
            label: 'マイフィード',
            icon: '👤',
            path: '/token-spending/my-feed',
          },
          {
            label: 'タイムライン',
            icon: '🕐',
            path: '/token-spending/timeline',
          },
          {
            label: 'トレンド',
            icon: '🔥',
            path: '/token-spending/trending',
          },
        ],
      },
      '/token-spending/timeline': {
        title: '支出フィード',
        subtitle: 'コミュニティのトークン使い道を共有し、透明性と信頼を育てる場所',
        subNavigationTabs: [
          {
            label: 'マイフィード',
            icon: '👤',
            path: '/token-spending/my-feed',
          },
          {
            label: 'タイムライン',
            icon: '🕐',
            path: '/token-spending/timeline',
          },
          {
            label: 'トレンド',
            icon: '🔥',
            path: '/token-spending/trending',
          },
        ],
      },
      '/token-spending/trending': {
        title: '支出フィード',
        subtitle: 'コミュニティのトークン使い道を共有し、透明性と信頼を育てる場所',
        subNavigationTabs: [
          {
            label: 'マイフィード',
            icon: '👤',
            path: '/token-spending/my-feed',
          },
          {
            label: 'タイムライン',
            icon: '🕐',
            path: '/token-spending/timeline',
          },
          {
            label: 'トレンド',
            icon: '🔥',
            path: '/token-spending/trending',
          },
        ],
      },
      '/subscriptions': {
        title: 'サブスクリプション',
        subtitle: '信任を前提とした定期循環',
      },
      '/dao-application': {
        title: 'DAO化申請',
        subtitle: '個から集合知への進化申請',
      },
      '/recruitments': {
        title: '募集一覧',
        subtitle: '意味のある仲間の求人情報',
      },
      '/badges': { title: 'バッジ一覧', subtitle: '貢献と信頼の証拠' },
      '/titles': { title: '肩書き一覧', subtitle: '実績に基づく称号システム' },
      '/book-reader-community': {
        title: '書籍読了システム',
        subtitle: '自律評価経済の理念を学ぶ',
      },
      '/chat': { title: 'チャットメニュー', subtitle: '意味ある対話の入口' },
      '/friends': { title: '友達リスト', subtitle: '信頼でつながる仲間たち' },
      '/activity': { title: 'アクティビティ', subtitle: '森の中での活動記録' },
      '/my-activity': {
        title: 'アクティビティ',
        subtitle: '森の中での活動記録',
      },
      '/desire-pool': {
        title: '需要の発酵池',
        subtitle: '需要の具現化を促すバイオーム',
      },
      '/create-desire': {
        title: '新しい需要を作成',
        subtitle: '森に新たな種を蒔く',
      },
      '/system-parameter-settings': {
        title: 'システムパラメータ設定',
        subtitle: 'DAO運営の基盤設定',
      },
      '/special-rewards': {
        title: '特別報酬設定',
        subtitle: '意味ある貢献への特別な評価',
      },
      '/practice-guide': {
        title: '真決DAO実践ガイド',
        subtitle: '理念を実践するための道しるべ',
      },
      '/profile': {
        title: 'マイページ',
        subtitle: '履歴が語る、あなたという森',
      },
    };

    // 動的パス（バッジ詳細、肩書き詳細など）の処理
    if (pathname.startsWith('/badges/') && pathname !== '/badges') {
      return {
        title: 'バッジ詳細',
        subtitle: 'バッジの詳細情報と保持者一覧',
        breadcrumb: {
          parent: '/badges',
          parentTitle: 'バッジ一覧',
        },
      };
    }

    if (pathname.startsWith('/titles/') && pathname !== '/titles') {
      return {
        title: '肩書き詳細',
        subtitle: '肩書きの詳細情報と保持者一覧',
        breadcrumb: {
          parent: '/titles',
          parentTitle: '肩書き一覧',
        },
      };
    }

    // 書籍読了システムの動的パス処理
    if (
      pathname.startsWith('/book-reader-community/') &&
      pathname !== '/book-reader-community'
    ) {
      if (pathname.includes('/certificate')) {
        return {
          title: '読了証明書',
          subtitle: '書籍読了の証明書',
          breadcrumb: {
            parent: '/book-reader-community',
            parentTitle: '書籍読了システム',
          },
        };
      } else if (pathname.includes('/chapter/')) {
        return {
          title: '章別クイズ',
          subtitle: '理解度確認クイズに挑戦',
          breadcrumb: {
            parent: '/book-reader-community',
            parentTitle: '書籍読了システム',
          },
        };
      } else {
        return {
          title: '書籍読了進捗',
          subtitle: '章別の読了状況と進捗管理',
          breadcrumb: {
            parent: '/book-reader-community',
            parentTitle: '書籍読了システム',
          },
        };
      }
    }

    // プロフィール画面の動的パス処理
    if (pathname.startsWith('/profile/')) {
      return { title: 'マイページ', subtitle: '履歴が語る、あなたという森' };
    }

    // 個人チャット画面の動的パス処理
    if (pathname.startsWith('/chat/') && pathname !== '/chat') {
      return {
        title: '個人チャット',
        subtitle: '1対1の対話空間',
        breadcrumb: {
          parent: '/chat',
          parentTitle: 'チャットメニュー',
        },
      };
    }

    // ストア編集画面の動的パス処理
    if (
      pathname.startsWith('/store/edit/') &&
      !pathname.includes('/items/')
    ) {
      return {
        title: 'ストア編集',
        subtitle: 'ストア情報を編集する',
        breadcrumb: {
          parent: '/my-stores',
          parentTitle: 'マイストア',
        },
      };
    }

    // 商品編集画面の動的パス処理
    if (
      pathname.startsWith('/store/') &&
      pathname.includes('/edit') &&
      pathname.includes('/items/')
    ) {
      return {
        title: '商品編集',
        subtitle: '商品情報を編集する',
        breadcrumb: {
          parent: '/mystore',
          parentTitle: 'マイストア',
        },
      };
    }

    // マイストア商品一覧画面
    if (pathname === '/my-store/items') {
      return {
        title: '商品管理',
        subtitle: '出品商品一覧・統計情報',
        breadcrumb: {
          parent: '/my-store',
          parentLabel: 'マイストア'
        }
      };
    }

    // マイストア商品管理画面のパンくず処理
    if (pathname.startsWith('/my-store/items/')) {
      const itemId = pathname.split('/')[3];
      const storeId = location.state?.storeId;
      
      return {
        title: '商品管理',
        subtitle: '商品の編集・プロセス管理',
        breadcrumb: storeId ? {
          parent: `/stores/${storeId}`,
          parentTitle: 'ストア詳細',
        } : {
          parent: '/my-store',
          parentTitle: 'マイストア',
        },
      };
    }

    // ストア詳細・商品詳細画面の動的パス処理
    if (
      pathname.startsWith('/stores/') &&
      pathname.includes('/items/') &&
      !pathname.includes('/edit')
    ) {
      // URL から storeId を抽出
      const storeIdMatch = pathname.match(/\/stores\/([^/]+)\/items/);
      const storeId = storeIdMatch ? storeIdMatch[1] : null;
      
      return {
        title: '商品詳細',
        subtitle: '商品の詳細情報',
        breadcrumb: {
          parent: storeId ? `/stores/${storeId}` : '/stores',
          parentTitle: 'ストア詳細',
          grandparent: '/stores',
          grandparentTitle: 'ストア一覧',
        },
      };
    }

    if (
      pathname.startsWith('/stores/') &&
      pathname !== '/stores' &&
      !pathname.includes('/items/')
    ) {
      // 遷移履歴を確認して適切な親ページを決定
      const history = navigationHistory || [];
      let parentPath = '/stores';
      let parentTitle = 'ストア一覧';

      // 履歴から現在のページ（ストア詳細）を除外して、直前のページを確認
      const historyWithoutCurrent = history.filter(
        (item) => !item.path.startsWith('/stores/') || item.path === '/stores'
      );
      
      if (historyWithoutCurrent.length > 0) {
        const previousPath = historyWithoutCurrent[historyWithoutCurrent.length - 1]?.path;
        if (previousPath && (previousPath.includes('/my-stores') || previousPath.includes('/mystore'))) {
          parentPath = '/mystore';
          parentTitle = 'マイストア';
        }
      }

      return {
        title: 'ストア詳細',
        subtitle: 'ストアの詳細情報と商品一覧',
        breadcrumb: {
          parent: parentPath,
          parentTitle: parentTitle,
        },
      };
    }

    // 動的ルートの処理
    if (pathname.startsWith('/projects/') && pathname.includes('/tasks')) {
      return {
        title: 'タスク管理',
        subtitle: 'プロジェクトタスクの包括的な管理',
        breadcrumb: {
          parent: '/projects',
          parentTitle: 'プロジェクト一覧',
        },
      };
    }
    if (
      pathname.startsWith('/projects/') &&
      pathname.includes('/issues')
    ) {
      const projectId = pathname.split('/')[2];
      return {
        title: '課題管理',
        subtitle: '発現したギャップの管理',
        breadcrumb: {
          parent: `/projects/${projectId}`,
          parentTitle: 'プロジェクト詳細',
        },
      };
    }
    if (
      pathname.startsWith('/projects/') &&
      pathname.includes('/risks')
    ) {
      const projectId = pathname.split('/')[2];
      return {
        title: 'リスク管理',
        subtitle: '潜在的リスクの事前把握',
        breadcrumb: {
          parent: `/projects/${projectId}`,
          parentTitle: 'プロジェクト詳細',
        },
      };
    }
    if (
      pathname.startsWith('/projects/') &&
      /^\/projects\/\d+$/.test(pathname)
    ) {
      return {
        title: 'プロジェクト詳細',
        subtitle: 'プロジェクト情報とDAOへの道筋',
        breadcrumb: {
          parent: '/projects',
          parentTitle: 'プロジェクト一覧',
        },
      };
    }

    // 問いの森詳細画面の動的パス処理（IDが数値の場合のみ）
    if (
      pathname.startsWith('/questions/') &&
      pathname.split('/').length === 3 &&
      /^\/questions\/\d+$/.test(pathname)
    ) {
      return {
        title: '問い詳細',
        subtitle: '問いの詳細と議論への参加',
        breadcrumb: {
          parent: '/question-forest',
          parentTitle: '問いの森',
        },
      };
    }

    // 募集詳細画面の動的パス処理
    if (pathname.startsWith('/recruitment/')) {
      const match = pathname.match(/^\/recruitment\/([^/]+)\/(\d+)$/);
      if (match) {
        const recruitmentType = match[1];
        const typeMap = {
          store: 'ストア募集詳細',
          dao: 'DAO募集詳細',
          task: 'タスク募集詳細',
        };
        return {
          title: typeMap[recruitmentType] || '募集詳細',
          subtitle: '募集の詳細情報と応募',
          breadcrumb: {
            parent: '/recruitments',
            parentTitle: '募集一覧',
          },
        };
      }
    }

    if (pathname === '/kindle-bonus') {
      return { title: '書籍読了特典受取', subtitle: '' };
    }

    return pageMap[pathname] || { title: '真決DAO', subtitle: '' };
  };

  const pageInfo = React.useMemo(
    () => getPageInfo(location.pathname),
    [location.pathname, navigationHistory]
  );

  const hideHeader = location.pathname === '/introduction' || location.pathname.startsWith('/feed/threads/');

  const noNavRoutes = ['/introduction', '/login', '/register', '/notifications'];
  const isGroupChatRoom = location.pathname.startsWith('/group-chat/');
  const isStoreChatRoom = location.pathname.startsWith('/store-chat/');
  const isFeedThreadDetail = location.pathname.startsWith('/feed/threads/');
  const showFullNav = !noNavRoutes.includes(location.pathname) && !isGroupChatRoom && !isStoreChatRoom && !isFeedThreadDetail;

  useEffect(() => {
    const checkUserAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
        } catch (error) {
          console.error('❌ Error parsing user data:', error);
          localStorage.removeItem('user');
          setUser(null);
        }
      } else {
        setUser(null);
      }

      setLoading(false);

      setTimeout(() => {
        setInitialLoadComplete(true);
      }, 100);
    };

    checkUserAuth();
  }, [location.pathname]);

  // メンテナンスモード検知
  // 平常時は無音（ポーリング無し）。
  // ユーザー操作のリクエスト失敗をaxios interceptorで拾ってメンテモード突入し、
  // その後だけ復旧検知ポーリングを動かす。
  useEffect(() => {
    let cancelled = false;
    const checkMaintenanceMode = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/health`, {
          timeout: 5000,
          headers: { Authorization: undefined },
        });
        if (cancelled) return;
        if (response.data?.maintenance === true) {
          setIsMaintenanceMode(true);
        } else {
          setIsMaintenanceMode(false);
        }
      } catch (error) {
        if (cancelled) return;
        setIsMaintenanceMode(true);
      }
    };

    // 初回マウント時の1回チェックのみ。以降はinterceptor任せ
    if (!isMaintenanceMode) {
      checkMaintenanceMode();
      return () => { cancelled = true; };
    }

    // メンテモード突入中のみ復旧検知ポーリング（10秒間隔）
    checkMaintenanceMode();
    const interval = setInterval(checkMaintenanceMode, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isMaintenanceMode]);

  // ユーザー操作経由のリクエスト失敗からメンテモード検知
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const isOutage =
          error?.code === 'ERR_NETWORK' ||
          error?.message === 'Network Error' ||
          status === 502 ||
          status === 503 ||
          status === 504;
        if (isOutage) {
          setIsMaintenanceMode(true);
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  useEffect(() => {
    const handleUserDataUpdated = (event) => {
      if (event.data?.type === 'userDataUpdated' && event.data?.detail) {
        setUser(event.data.detail);
      }
    };

    window.addEventListener('message', handleUserDataUpdated);
    return () => window.removeEventListener('message', handleUserDataUpdated);
  }, []);

  // モバイル判定のためのウィンドウサイズ監視
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // マウント後に実行
    setIsMounted(true);
    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // メニューの外側をクリックした時に閉じる
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('[data-mobile-menu]')) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMobileMenuOpen]);

  // Magic Link認証後の状態同期のためのストレージイベントリスナー
  useEffect(() => {
    const handleStorageChange = (event) => {
      // Magic Link認証でトークンが追加された場合の処理
      if (event.key === 'token' && event.newValue && !event.oldValue) {
        // ユーザー情報も同時に更新されているかチェック
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            setUser(userData);
          } catch (error) {
            console.error('❌ ユーザーデータの解析エラー:', error);
          }
        }
      }

      // トークンが削除された場合（ログアウト）
      if (event.key === 'token' && !event.newValue && event.oldValue) {
        setUser(null);
      }

      // ユーザー情報が更新された場合
      if (event.key === 'user' && event.newValue) {
        try {
          const userData = JSON.parse(event.newValue);
          setUser(userData);
        } catch (error) {
          console.error('❌ ユーザーデータの解析エラー:', error);
        }
      }
    };

    // カスタムイベントリスナー（同一タブ内での更新用）
    const handleAuthUpdate = (event) => {
      const { token, user } = event.detail;

      if (token && user) {
        setUser(user);
      }
    };

    // ストレージイベントリスナー（他のタブからの変更用）
    window.addEventListener('storage', handleStorageChange);

    // カスタムイベントリスナー（同一タブ内の変更用）
    window.addEventListener('authUpdated', handleAuthUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authUpdated', handleAuthUpdate);
    };
  }, []);

  useEffect(() => {
    const fetchUserAndCart = async () => {
      const token = localStorage.getItem('token');
      const existingUser = localStorage.getItem('user');

      if (!token) return;

      // 既にユーザー情報がある場合は、カート情報のみ取得
      // ただし、actor情報がない場合は再取得する
      if (existingUser) {
        try {
          const userData = JSON.parse(existingUser);
          
          // actor情報がない場合は再取得が必要
          if (!userData.actor || !userData.actor.actor_id) {
            console.log('🔄 actor情報を再取得します');
            // existingUserをnullにして再取得ロジックに進む
            localStorage.removeItem('user');
          } else {
            // App-NoWallet - Using existing localStorage user
            setUser(userData);

            // カート情報取得（本会員以上のみ）
            if (userData.permission_level >= 3) {
              try {
                const config = { headers: { Authorization: `Bearer ${token}` } };
                const cartResponse = await axios.get(
                  `${API_URL}/api/cart/list`,
                  config
                );

                if (cartResponse.data?.success && cartResponse.data?.cartItems) {
                  setCartItemCount(cartResponse.data.cartItems.length);
                }
              } catch (cartError) {
                // カート・ネットワークエラーは静音化（サーバー未起動時）
                if (
                  cartError.response?.status !== 401 &&
                  !cartError.message?.includes('Network Error') &&
                  !cartError.message?.includes('Failed to fetch')
                ) {
                  console.warn(
                    '⚠️ カート情報取得エラー（非致命的）:',
                    cartError.response?.status
                  );
                }
                setCartItemCount(0);
              }
            } else {
              // 準会員以下はカート機能未対応
              setCartItemCount(0);
            }
            return;
          }
        } catch (error) {
          // Error parsing existing user data
          // パースエラーの場合のみAPI再取得に進む
        }
      }

      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        // /api/auth/me を先に呼び出す
        const userResponse = await axios.get(`${API_URL}/api/auth/me`, config);
        
        // ユーザー情報取得後、権限レベルに応じてカート情報を取得
        let cartResponse = null;
        if (userResponse.data && userResponse.data.permission_level >= 3) {
          try {
            cartResponse = await axios.get(`${API_URL}/api/cart/list`, config);
          } catch (cartError) {
            // カートエラーは無視（権限不足の可能性）
            cartResponse = { data: { success: false, cartItems: [] } };
          }
        } else {
          cartResponse = { data: { success: false, cartItems: [] } };
        }

        if (userResponse.data) {
          // App-NoWallet - Raw API response received

          console.log('🔍 App-NoWallet - API Response:', {
            iconUrl: userResponse.data.iconUrl,
            actorProfileImageUrl: userResponse.data.actor?.profile_image_url,
            hasActor: !!userResponse.data.actor,
          });

          const userData = {
            id: userResponse.data.id || userResponse.data.user_id,
            user_id: userResponse.data.user_id || userResponse.data.id,
            username: userResponse.data.username,
            name: userResponse.data.name,
            email: userResponse.data.email,
            iconUrl: userResponse.data.iconUrl || userResponse.data.actor?.profile_image_url || '/default-avatar.svg',
            profile_image_url: userResponse.data.actor?.profile_image_url,
            isAdmin: userResponse.data.role === 'admin',
            role: userResponse.data.role,
            permission_level: userResponse.data.permission_level || 0,
            kycStatus: userResponse.data.kycStatus,
            actor: userResponse.data.actor,
            actor_id: userResponse.data.actor?.actor_id || userResponse.data.actor?.id,
            actorId: userResponse.data.actor?.actor_id || userResponse.data.actor?.id,
          };

          console.log('🔍 App-NoWallet - Processed userData:', {
            iconUrl: userData.iconUrl,
            profileImageUrl: userData.profile_image_url,
          });

          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));

          // App-NoWallet - Verification - localStorage user saved
        }

        if (cartResponse.data?.success && cartResponse.data?.cartItems) {
          setCartItemCount(cartResponse.data.cartItems.length);
        }
      } catch (error) {
        // App-NoWallet - API error occurred

        if (error.response?.status === 401) {
          // 401エラーは静かにハンドリング（トークン無効・期限切れ）
          // ウォレット不一致エラーの場合のみアラート表示
          if (error.response?.data?.code === 'WALLET_MISMATCH') {
            // ウォレットアドレス不一致検出 - ログアウトします
            toast.error(
              'ウォレットアドレスの不一致が検出されました。\n再ログインが必要です。'
            );
          }
          // トークンをクリアして未認証状態にリセット
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('actorId');
          setUser(null);
          // イントロダクション画面での401は正常な動作なのでhandleLogoutは呼ばない
        } else if (!error.message?.includes('Network Error')) {
          // ネットワークエラー以外のエラーのみログ出力
          console.warn('API呼び出しエラー:', error.message);
        }
      }
    };

    fetchUserAndCart();
  }, []);

  // 地球儀アイコンクリック時の動作は削除

  const handleLogout = () => {
    // ストアとして操作中に取っておいた切り替え前のトークンも消す（ログアウト後にオーナーへ戻れないように）
    clearStoreActing();
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('user');
    localStorage.removeItem('actorId');
    localStorage.removeItem('walletAddress');
    localStorage.removeItem('kycCompleted');
    setUser(null);
    navigate('/introduction');
  };

  // 初期ローディング状態：完全に空白を表示してちらつきを防止
  if (loading || !initialLoadComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  // メンテナンスモード時はメンテナンス画面を表示
  if (isMaintenanceMode) {
    return <MaintenanceScreen />;
  }

  return (
    <div className="App">
      {showFullNav && !hideHeader && 
        <nav className="bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
                {/* プロフィールアイコン（左上に移動） */}
                {(() => {
                  const getPermissionFromToken = () => {
                    try {
                      const token = localStorage.getItem('token');
                      if (!token) return 1;
                      const parts = token.split('.');
                      if (parts.length === 3) {
                        const base64Url = parts[1];
                        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                        const jsonPayload = decodeURIComponent(
                          window.atob(base64).split('').map(c => 
                            '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                          ).join('')
                        );
                        const tokenData = JSON.parse(jsonPayload);
                        return tokenData.permission_level || 1;
                      }
                    } catch (error) {
                      console.error('JWT decode error:', error);
                    }
                    return 1;
                  };

                  const permLevel = getPermissionFromToken();
                  if (permLevel >= 2 && user?.actor) {
                    const displayName = user.actor.display_name || user.username || user.name || 'ユーザー';
                    return (
                      <button
                        onClick={() => {
                          openProfile({
                            id: user.actor?.actor_id || user.actorId,
                            actor_id: user.actor?.actor_id || user.actorId,
                            actorId: user.actor?.actor_id || user.actorId,
                            display_name: displayName,
                            displayName: displayName,
                            profile_image_url: user.iconUrl || user.profile_image_url,
                            bio: user.actor?.bio || user.bio || user.description || '',
                            actorType: user.actorType || 'user',
                            lastActiveAt: user.lastActiveAt,
                          });
                        }}
                        className="hover:opacity-80 transition-opacity -ml-2"
                        title={permLevel === 2 ? `${displayName} (準会員)` : displayName}
                      >
                        <ActorAvatar
                          src={user.iconUrl || user.profile_image_url}
                          alt={displayName}
                          actorType={user.actorType || 'user'}
                          actorStatus="active"
                          size="md"
                          noPadding={true}
                        />
                      </button>
                    );
                  }
                  return null;
                })()}
                {pageInfo.title !== '真決DAO' && 
                  <>
                    <div className="text-white/60 hidden sm:block">|</div>
                    <div className="min-w-0 flex-1">
                      <div
                        className="flex items-center font-semibold text-white overflow-hidden"
                        style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1.125rem)' }}
                      >
                        {/* パンくずナビゲーション */}
                        {(() => {
                          // 詳細ページの場合は静的なパンくずを優先表示
                          if (pageInfo.breadcrumb) {
                            return (
                              <>
                                {pageInfo.breadcrumb.grandparent && (
                                  <>
                                    <Link
                                      to={pageInfo.breadcrumb.grandparent}
                                      className="text-yellow-300 hover:text-yellow-100 transition-colors text-sm hidden sm:inline-block"
                                    >
                                      {pageInfo.breadcrumb.grandparentTitle}
                                    </Link>
                                    <span className="mx-2 text-white/60 hidden sm:inline-block">&gt;</span>
                                  </>
                                )}
                                <Link
                                  to={pageInfo.breadcrumb.parent}
                                  className="text-yellow-300 hover:text-yellow-100 transition-colors text-sm"
                                >
                                  {pageInfo.breadcrumb.parentTitle}
                                </Link>
                                <span className="mx-2 text-white/60">&gt;</span>
                                <span className="truncate">
                                  {pageInfo.title}
                                </span>
                              </>
                            );
                          }

                          // 通常は遷移履歴ベースのパンくず
                          const breadcrumbs = getBreadcrumbs();
                          const currentPath = location.pathname;

                          // 現在のページとダッシュボードを除いた履歴を表示
                          const excludedPaths = [
                            '/dashboard',
                            '/introduction',
                            '/login',
                            '/register',
                            '/kyc',
                          ];
                          const historyToShow = breadcrumbs.filter(
                            (item) =>
                              item.path !== currentPath &&
                              !excludedPaths.includes(item.path)
                          );

                          return (
                            <div className="flex items-center min-w-0">
                              {historyToShow.map((item) => 
                                <React.Fragment key={item.path}>
                                  <Link
                                    to={item.path}
                                    className="text-yellow-300 hover:text-yellow-100 transition-colors text-xs sm:text-sm truncate hidden sm:inline-block"
                                  >
                                    {item.title}
                                  </Link>
                                  <span className="mx-1 sm:mx-2 text-white/60 hidden sm:inline-block">
                                    &gt;
                                  </span>
                                </React.Fragment>
                              )}
                              <span className="truncate">{pageInfo.title}</span>
                            </div>
                          );
                        })()}
                      </div>
                      {pageInfo.subtitle && 
                        <div className="text-xs sm:text-sm text-white/80 hidden sm:block truncate">
                          {pageInfo.subtitle}
                        </div>
                      }
                    </div>
                  </>
                }
              </div>

              <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
                {user ? 
                  <>
                    {/* トークン表示は常に表示（デスクトップ・モバイル共通） */}
                    {(() => {
                      // JWTトークンから権限レベルを取得（信頼できる情報源）
                      const getPermissionFromToken = () => {
                        try {
                          const token = localStorage.getItem('token');
                          if (!token) return 1;
                          const parts = token.split('.');
                          if (parts.length === 3) {
                            const base64Url = parts[1];
                            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                            const jsonPayload = decodeURIComponent(
                              window.atob(base64).split('').map(c => 
                                '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                              ).join('')
                            );
                            const tokenData = JSON.parse(jsonPayload);
                            return tokenData.permission_level || 1;
                          }
                        } catch (error) {
                          console.error('JWT decode error:', error);
                        }
                        return 1;
                      };
                      
                      const permLevel = getPermissionFromToken();
                      return permLevel >= 3 ? (
                        <div>
                          <TokenDisplay />
                        </div>
                      ) : null;
                    })()}

                    {/* 通知ベル（デスクトップ・モバイル共通） */}
                    {(() => {
                      const getPermissionFromToken = () => {
                        try {
                          const token = localStorage.getItem('token');
                          if (!token) return 1;
                          const parts = token.split('.');
                          if (parts.length === 3) {
                            const base64Url = parts[1];
                            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                            const jsonPayload = decodeURIComponent(
                              window.atob(base64).split('').map(c => 
                                '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                              ).join('')
                            );
                            const tokenData = JSON.parse(jsonPayload);
                            return tokenData.permission_level || 1;
                          }
                        } catch (error) {
                          return 1;
                        }
                        return 1;
                      };
                      
                      const permLevel = getPermissionFromToken();
                      return permLevel >= 3 ? (
                        <div>
                          <NotificationBell API_URL={API_URL} />
                        </div>
                      ) : null;
                    })()}

                    {/* カート表示（デスクトップ・モバイル共通） */}
                    {(() => {
                      const getPermissionFromToken = () => {
                        try {
                          const token = localStorage.getItem('token');
                          if (!token) return 1;
                          const parts = token.split('.');
                          if (parts.length === 3) {
                            const base64Url = parts[1];
                            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                            const jsonPayload = decodeURIComponent(
                              window.atob(base64).split('').map(c => 
                                '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                              ).join('')
                            );
                            const tokenData = JSON.parse(jsonPayload);
                            return tokenData.permission_level || 1;
                          }
                        } catch (error) {
                          return 1;
                        }
                        return 1;
                      };
                      
                      const permLevel = getPermissionFromToken();
                      return permLevel >= 3 && isMounted ? (
                        <div>
                          <CartIcon count={cartItemCount} />
                        </div>
                      ) : null;
                    })()}
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      {/* ゲストユーザー向けの登録ボタン */}
                      {(() => {
                        // JWTトークンから権限レベルを取得
                        const getPermissionFromToken = () => {
                          try {
                            const token = localStorage.getItem('token');
                            if (!token) return 1;
                            const parts = token.split('.');
                            if (parts.length === 3) {
                              const base64Url = parts[1];
                              const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                              const jsonPayload = decodeURIComponent(
                                window.atob(base64).split('').map(c => 
                                  '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                                ).join('')
                              );
                              const tokenData = JSON.parse(jsonPayload);
                              return tokenData.permission_level || 1;
                            }
                          } catch (error) {
                            return 1;
                          }
                          return 1;
                        };
                        
                        const permLevel = getPermissionFromToken();
                        
                        if (user.role === 'guest' || permLevel === 1) {
                          return (
                            <Link
                              to="/signin"
                              className="bg-white/20 hover:bg-white/30 text-white font-medium py-1 px-2 sm:py-2 sm:px-4 rounded-full text-xs sm:text-sm transition-all duration-300 flex items-center space-x-1 sm:space-x-2"
                              title="メール認証で投稿・共鳴機能を解放"
                            >
                              <span className="text-sm sm:text-base">✍</span>
                              <span>登録</span>
                            </Link>
                          );
                        } else if (permLevel === 2) {
                          return (
                            <Link
                              to="/dashboard"
                              className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-full transition-colors"
                              title="ダッシュボードに戻る"
                            >
                              <FiHome size={18} />
                            </Link>
                          );
                        }
                        return null;
                      })()}
                      {(() => {
                        const getPermissionFromToken = () => {
                          try {
                            const token = localStorage.getItem('token');
                            if (!token) return 1;
                            const parts = token.split('.');
                            if (parts.length === 3) {
                              const base64Url = parts[1];
                              const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                              const jsonPayload = decodeURIComponent(
                                window.atob(base64).split('').map(c => 
                                  '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                                ).join('')
                              );
                              const tokenData = JSON.parse(jsonPayload);
                              return tokenData.permission_level || 1;
                            }
                          } catch (error) {
                            return 1;
                          }
                          return 1;
                        };
                        
                        const permLevel = getPermissionFromToken();
                        
                        if (permLevel === 2) {
                          // 準会員向けKYC登録ボタン
                          return (
                            <Link
                              to="/kyc"
                              className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-1 px-2 sm:py-2 sm:px-4 rounded-full text-xs sm:text-sm transition-all duration-300 flex items-center space-x-1 sm:space-x-2"
                              title="KYC認証を完了して本会員になる"
                            >
                              <span className="text-sm sm:text-base">🌱</span>
                              <span>本登録</span>
                            </Link>
                          );
                        }
                        return null;
                      })()}

                      {/* デスクトップ版: カート、ガイド、ログアウトを個別表示 */}
                      {isMounted && !isMobile && 
                        <>
                          {/* ヘルプ・実践ガイドアイコン */}
                          <Link
                            to="/practice-guide"
                            className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-full transition-colors relative flex flex-col items-center justify-center -space-y-1"
                            title="真決DAO実践ガイド"
                          >
                            <span className="text-lg">📖</span>
                            <span className="text-xs font-bold text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                              ガイド
                            </span>
                          </Link>

                          <div className="flex items-center space-x-1 sm:space-x-2 bg-white/10 px-2 sm:px-3 py-1 rounded-md">
                            <div className="text-white text-xs hidden sm:block">
                              {user.role === 'guest'
                                ? 'ゲスト'
                                : localStorage
                                    .getItem('walletAddress')
                                    ?.slice(0, 3)}
                            </div>
                            <button
                              className="text-white/80 hover:text-white hover:bg-white/20 p-1 rounded transition-colors"
                              onClick={handleLogout}
                              title="ログアウト"
                            >
                              <FiLogOut size={14} />
                            </button>
                          </div>
                        </>
                      }

                      {/* モバイル版: ハンバーガーメニューボタン */}
                      {isMounted && isMobile && 
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsMobileMenuOpen(!isMobileMenuOpen);
                          }}
                          className="relative p-2 text-white hover:bg-white/20 rounded-full transition-colors bg-white/10"
                          title="メニュー"
                          data-mobile-menu
                        >
                          {isMobileMenuOpen ? 
                            <FiX size={18} className="text-white" />
                           : 
                            <FiMenu size={18} className="text-white" />
                          }
                        </button>
                      }
                    </div>
                  </>
                 : 
                  <></>
                }
              </div>
            </div>
          </div>

          {/* モバイルメニュードロップダウン */}
          {isMounted && isMobile && isMobileMenuOpen && user && 
            <div
              className="absolute top-full right-4 z-50 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden min-w-[160px] max-w-[200px]"
              data-mobile-menu
            >
              <div className="py-2">
                {/* カート */}
                {(() => {
                  // JWTトークンから権限レベルを取得
                  const getPermissionFromToken = () => {
                    try {
                      const token = localStorage.getItem('token');
                      if (!token) return 1;
                      const parts = token.split('.');
                      if (parts.length === 3) {
                        const base64Url = parts[1];
                        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                        const jsonPayload = decodeURIComponent(
                          window.atob(base64).split('').map(c => 
                            '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                          ).join('')
                        );
                        const tokenData = JSON.parse(jsonPayload);
                        return tokenData.permission_level || 1;
                      }
                    } catch (error) {
                      return 1;
                    }
                    return 1;
                  };
                  
                  const permLevel = getPermissionFromToken();
                  return permLevel >= 3 ? (
                    <>
                      <Link
                        to="/cart"
                        className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center space-x-3 text-sm font-medium"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <FiShoppingCart size={16} />
                        <span>カート</span>
                      </Link>
                      <Link
                        to="/purchase-history"
                        className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center space-x-3 text-sm font-medium"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <FiPackage size={16} />
                        <span>購入履歴</span>
                      </Link>
                    </>
                  ) : null;
                })()}

                {/* 実践ガイド */}
                <Link
                  to="/practice-guide"
                  className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center space-x-3 text-sm font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <FiBookOpen size={16} />
                  <span>実践ガイド</span>
                </Link>

                {/* 報酬受取 */}
                <Link
                  to="/my-rewards"
                  className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center space-x-3 text-sm font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <FiPackage size={16} />
                  <span>報酬受取</span>
                </Link>

                {/* システム管理（管理者のみ） */}
                {(() => {
                  // JWTトークンから権限レベルを取得
                  const getPermissionFromToken = () => {
                    try {
                      const token = localStorage.getItem('token');
                      if (!token) return 1;
                      const parts = token.split('.');
                      if (parts.length === 3) {
                        const base64Url = parts[1];
                        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                        const jsonPayload = decodeURIComponent(
                          window.atob(base64).split('').map(c => 
                            '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                          ).join('')
                        );
                        const tokenData = JSON.parse(jsonPayload);
                        return tokenData.permission_level || 1;
                      }
                    } catch (error) {
                      return 1;
                    }
                    return 1;
                  };
                  
                  const permLevel = getPermissionFromToken();
                  return permLevel >= 4 ? (
                    <Link
                      to="/admin/mining-debug"
                      className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center space-x-3 text-sm font-medium"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <FiSettings size={16} />
                      <span>システム管理</span>
                    </Link>
                  ) : null;
                })()}

                {/* 区切り線 */}
                <hr className="my-1 border-gray-200" />

                {/* ログアウト */}
                <div
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleLogout();
                  }}
                  className="block w-full cursor-pointer px-4 py-3 text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center space-x-3 text-sm font-medium border-0 outline-none"
                >
                  <FiLogOut size={16} />
                  <span>ログアウト</span>
                </div>
              </div>
            </div>
          }
        </nav>
      }

      {/* サブナビゲーションタブ */}
      {showFullNav && !hideHeader && pageInfo.subNavigationTabs && 
        <div className="bg-white border-b border-gray-200 sticky top-16 z-40 shadow-sm">
          <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
            <div className="flex space-x-1">
              {pageInfo.subNavigationTabs.map((tab) => {
                const isActive = location.pathname === tab.path;
                return (
                  <Link
                    key={tab.path}
                    to={tab.path}
                    className={`flex items-center space-x-1 px-3 py-2 text-xs font-medium transition-colors border-b-2 ${
                      isActive
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm">{tab.icon}</span>
                    <span>{tab.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      }

      <main 
        className={`${showFullNav && !hideHeader ? 'main-content' : ''} ${location.pathname === '/kyc' ? 'overflow-hidden' : ''}`}
        style={{ 
          height: showFooter 
            ? 'calc(100vh - 128px)'  // ナビゲーションバー(64px) + モバイルフッター(64px)
            : showFullNav && !hideHeader 
              ? 'calc(100vh - 64px)'   // ナビゲーションバーのみ(64px)
              : '100vh',                // ヘッダーなし
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        {/* ストアとして操作中なら上に出す（BOARD A-33） */}
        <ActingAsStoreBanner />
        <React.Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-blue-50">
              <div className="text-center">
                <div className="text-4xl mb-4">🌱</div>
                <div className="text-lg font-semibold text-gray-700">
                  読み込み中...
                </div>
                <div className="text-sm text-gray-500 mt-2">
                  真決DAOを準備しています
                </div>
              </div>
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<AuthRedirect />} />
            <Route path="/introduction" element={<Introduction />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/admin/mining-debug" element={<AdminMiningDebug />} />
            <Route path="/admin/kyc-verification/:userId" element={<KYCVerification />} />
            <Route path="/admin/dao-applications/:applicationId" element={<DaoApplicationReview />} />
            <Route
              path="/auth/verify-magic-link"
              element={<VerifyMagicLink />}
            />
            <Route path="/auth/wallet-recovery" element={<WalletRecovery />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/dashboard"
              element={
                loading ? 
                  <div className="loading-screen">Loading...</div>
                 : localStorage.getItem('token') &&
                  localStorage.getItem('user') ? 
                  <React.Suspense
                    fallback={
                      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-blue-50">
                        <div className="text-center">
                          <div className="text-3xl mb-3">🌳</div>
                          <div className="text-lg font-semibold text-gray-700">
                            ダッシュボード準備中...
                          </div>
                          <div className="text-sm text-gray-500 mt-2">
                            しばらくお待ちください
                          </div>
                        </div>
                      </div>
                    }
                  >
                    <ErrorBoundary>
                      <DashboardContainer />
                    </ErrorBoundary>
                  </React.Suspense>
                 : 
                  <Navigate to="/introduction" />
                
              }
            />
            <Route
              path="/dashboard-old"
              element={
                loading ? 
                  <div className="loading-screen">Loading...</div>
                 : localStorage.getItem('token') &&
                  localStorage.getItem('user') ? 
                  <React.Suspense
                    fallback={
                      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-blue-50">
                        <div className="text-center">
                          <div className="text-3xl mb-3">🌳</div>
                          <div className="text-lg font-semibold text-gray-700">
                            旧ダッシュボード準備中...
                          </div>
                          <div className="text-sm text-gray-500 mt-2">
                            しばらくお待ちください
                          </div>
                        </div>
                      </div>
                    }
                  >
                    <ErrorBoundary>
                      <ActorIntegratedDashboard />
                    </ErrorBoundary>
                  </React.Suspense>
                 : 
                  <Navigate to="/introduction" />
                
              }
            />
            <Route
              path="/dashboard/forest"
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  <ErrorBoundary>
                    <ForestCategoryPage />
                  </ErrorBoundary>
                </React.Suspense>
              }
            />
            <Route
              path="/dashboard/projects"
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  <ErrorBoundary>
                    <ProjectsCategoryPage />
                  </ErrorBoundary>
                </React.Suspense>
              }
            />
            <Route
              path="/dashboard/store"
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  <ErrorBoundary>
                    <StoreCategoryPage />
                  </ErrorBoundary>
                </React.Suspense>
              }
            />
            <Route
              path="/dashboard/social"
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  <ErrorBoundary>
                    <SocialCategoryPage />
                  </ErrorBoundary>
                </React.Suspense>
              }
            />
            <Route
              path="/dashboard/applications"
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  <ErrorBoundary>
                    <ApplicationsCategoryPage />
                  </ErrorBoundary>
                </React.Suspense>
              }
            />
            <Route
              path="/dashboard/admin"
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  <ErrorBoundary>
                    <AdminCategoryPage />
                  </ErrorBoundary>
                </React.Suspense>
              }
            />
            <Route path="/profile/:actorId" element={<Profile />} />
            <Route
              path="/profile/:id/evaluation-history"
              element={<MyStakes />}
            />
            <Route path="/trust-network/:userId" element={<TrustNetwork />} />
            <Route
              path="/discussions"
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  <ErrorBoundary>
                    <AllDiscussions />
                  </ErrorBoundary>
                </React.Suspense>
              }
            />
            <Route
              path="/discussion/:type/:itemId"
              element={<IntegratedDiscussion />}
            />
            <Route path="/wallet" element={<Wallet />} />
            <Route path="/actor-network" element={<ActorNetworkView />} />
            <Route
              path="/actor-network/:actorId"
              element={<ActorNetworkView />}
            />
            <Route path="/actor-network/:id" element={<ActorNetworkView />} />
            <Route path="/timeline" element={<Timeline />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/token-spending" element={<Navigate to="/token-spending/timeline" replace />} />
            <Route path="/token-spending/my-feed" element={<TokenSpendingMyFeed />} />
            <Route path="/token-spending/timeline" element={<TokenSpendingTimeline />} />
            <Route path="/token-spending/trending" element={<TokenSpendingTrending />} />
            <Route path="/feed/threads/:threadId" element={<FeedThreadDetail />} />
            <Route path="/mystore" element={<MyStoreView />} />
            <Route path="/my-stores" element={<MyStoreView />} />
            <Route path="/my-store" element={<MyStoreView />} />
            <Route path="/my-store/items" element={<MyStoreItemsManagement />} />
            <Route path="/my-store/items/:itemId" element={<MyStoreItemManagement />} />
            <Route path="/store-orders" element={<StoreOrderManagement />} />
            <Route path="/store/edit/:storeId" element={<StoreEdit />} />
            <Route path="/question-forest" element={<QuestionForest />} />
            <Route path="/questions/:questionId" element={<QuestionDetail />} />
            <Route path="/kindle-bonus" element={<KindleBonus />} />
            <Route
              path="/forest-ecosystem"
              element={<ForestEcosystemVisualization />}
            />
            <Route path="/purchase-history" element={<PurchaseHistory />} />
            <Route path="/stores" element={<StoreListView />} />
            <Route path="/stores/:storeId" element={<StoreDetail />} />
            <Route
              path="/stores/:storeId/items/:itemId"
              element={<StoreItemDetailView />}
            />
            <Route
              path="/user-items/:itemId"
              element={<StoreItemDetailView />}
            />
            <Route
              path="/store/:storeId/edit"
              element={<StoreItemEditView />}
            />
            <Route path="/store/items/new" element={<StoreItemEditView />} />
            <Route
              path="/store/items/edit/:itemId"
              element={<StoreItemEditView />}
            />
            <Route
              path="/stores/:storeId/recruitments"
              element={<StoreRecruitmentSettings />}
            />
            <Route
              path="/store-recruitment-settings/:storeId"
              element={<StoreRecruitmentSettings />}
            />
            <Route path="/product-search" element={<ProductSearch />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/store-search" element={<StoreSearch />} />
            // 需要の発酵池は/desire-poolに統一 // 需要詳細は必要に応じて復元
            <Route path="/daos" element={<DaoListView />} />
            <Route path="/daos/:daoId" element={<DaoDetail />} />
            <Route path="/dao/:daoId" element={<DaoDetail />} />
            <Route path="/daos/:daoId/members" element={<DaoMembers />} />
            <Route
              path="/daos/:daoId/recruitments"
              element={<DaoRecruitmentSettings />}
            />
            <Route
              path="/dao-recruitment-settings/:daoId"
              element={<DaoRecruitmentSettings />}
            />
            <Route path="/daos/:daoId/governance" element={<DaoGovernance />} />
            <Route path="/dao/:daoId/governance" element={<DaoGovernance />} />
            <Route path="/daos/:daoId/apply" element={<DaoApplication />} />
            <Route
              path="/dao/create-derived"
              element={<DerivedDaoCreateView />}
            />
            <Route path="/dao/derived-list" element={<DerivedDaoListView />} />
            <Route path="/chat" element={<ChatMenu />} />
            <Route path="/group-chat" element={<GroupChatList />} />
            <Route path="/group-chat/:groupId" element={<GroupChatRoom />} />
            <Route path="/store-chat/:chatGroupId" element={<StoreChat />} />
            <Route path="/special-rewards" element={<SpecialRewards />} />
            <Route
              path="/special-rewards/:rewardId"
              element={<SpecialRewardDetail />}
            />
            <Route
              path="/special-rewards/kindle"
              element={<SpecialRewards />}
            />
            <Route
              path="/special-rewards/invite"
              element={<SpecialRewards />}
            />
            <Route path="/practice-guide" element={<PracticeGuide />} />
            <Route
              path="/store-application"
              element={<StoreApplicationView />}
            />
            <Route
              path="/store-application/:id"
              element={<StoreApplicationDetailView />}
            />
            <Route path="/store-appeal/:id" element={<StoreAppealView />} />
            <Route
              path="/store-application-status"
              element={<StoreApplicationStatusView />}
            />
            <Route path="/dao-application" element={<DaoApplication />} />
            <Route path="/dao/invitations" element={<DaoInvitations />} />
            <Route path="/applications" element={<ApplicationsList />} />
            <Route path="/projects" element={<ProjectList />} />
            <Route path="/project/:project_id" element={<ProjectDetail />} />
            <Route path="/projects/:projectId" element={<ProjectDetail />} />
            <Route
              path="/projects/:projectId/tasks"
              element={<TaskManagement />}
            />
            <Route
              path="/projects/:projectId/manage"
              element={<ProjectManagement />}
            />
            <Route
              path="/projects/:projectId/issues"
              element={<IssueManagement />}
            />
            <Route
              path="/projects/:projectId/risks"
              element={<RiskManagement />}
            />
            <Route
              path="/projects/:projectId/issues/new"
              element={<IssueDetail />}
            />
            <Route
              path="/projects/:projectId/risks/new"
              element={<RiskDetail />}
            />
            <Route
              path="/projects/:projectId/issues/:issueId"
              element={<IssueDetail />}
            />
            <Route
              path="/projects/:projectId/risks/:riskId"
              element={<RiskDetail />}
            />
            <Route path="/realization-judge" element={<RealizationJudge />} />
            <Route path="/badges" element={<BadgeHub />} />
            <Route path="/badges/:badgeCode" element={<BadgeDetail />} />
            <Route
              path="/badges/:badgeCode/holders"
              element={<BadgeHolders />}
            />
            <Route path="/titles" element={<TitleHub />} />
            <Route path="/titles/:titleCode" element={<TitleDetail />} />
            <Route path="/book-reader-community" element={<BookReadingHub />} />
            <Route
              path="/book-reader-community/:bookCode"
              element={<BookReadingProgress />}
            />
            <Route
              path="/book-reader-community/:bookCode/chapter/:chapterNumber"
              element={<ChapterQuiz />}
            />
            <Route
              path="/book-reader-community/:bookCode/certificate"
              element={<BookCertificate />}
            />
            <Route path="/subscriptions" element={<Subscriptions />} />
            <Route path="/desire-pool" element={<DesirePool />} />
            <Route path="/recruitments" element={<RecruitmentListView />} />
            <Route path="/tasks/:taskId/recruitments/new" element={<TaskRecruitmentForm />} />
            <Route
              path="/recruitment/:type/:recruitmentId"
              element={<RecruitmentDetail />}
            />
            <Route path="/friends" element={<FriendsList />} />
            <Route path="/activity" element={<Activity />} />
            <Route path="/my-activity" element={<Activity />} />
            <Route path="/mystakes" element={<MyStakes />} />
            <Route
              path="/system-parameter-settings"
              element={<SystemParameterSettings />}
            />
            <Route path="/users/:userId" element={<UserDetail />} />
            <Route path="/posts/:postId" element={<PostDetail />} />
            <Route path="/transfer-entry" element={<TransferEntryForm />} />
            <Route path="/badge-request" element={<BadgeRequest />} />
            <Route path="/search" element={<SearchIntegration />} />
            <Route path="/kyc" element={<Kyc />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </React.Suspense>
      </main>

      {/* モバイルフッター */}
      {showFooter && <MobileFooter />}

      {/* トークンアニメーションエフェクト */}
      <AnimationRenderer />

      {/* グローバルマイニングエフェクト */}
      <GlobalMiningEffect />

      {/* Toast通知コンテナ（独自実装） */}
      <ToastContainer />

      {/* プロフィールサイドバー（グローバル） */}
      <ProfileSidebar 
        currentUser={user}
        onTrust={(profile) => {
          openBottomSheet({
            title: '🤝 信任する',
            height: 'full',
            headerColor: 'green',
            content: (
              <TrustForm
                targetActor={profile}
                context="profile"
                contextId={profile.actor_id || profile.actorId}
                onSuccess={(data) => {
                  toast.success(`${data.trust_target_display_name}への信任が完了しました`);
                }}
              />
            ),
          });
        }}
        onChat={(profile) => {
          navigate(`/chat`);
        }}
      />
      
      <BottomSheet />
    </div>
  );
};

export default App;
